import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Select, MenuItem, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo, useRef, useState } from "react";
import InputField from "../InputField";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import formatDateToYYYYMMDD from "../../Utils/dateFormat";

const CustomHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  maxDate,
  months,
}) => {
  const years = Array.from(
    { length: 100 },
    (_, i) => new Date().getFullYear() - i
  );

  const maxYear = maxDate ? maxDate.getFullYear() : null;
  const maxMonth = maxDate ? maxDate.getMonth() : null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 8px",
        gap: "8px",
      }}
    >
      <Box
        onClick={decreaseMonth}
        sx={{
          pointerEvents: prevMonthButtonDisabled ? "none" : "auto",
          cursor: "pointer",
          opacity: prevMonthButtonDisabled ? 0.3 : 1,
        }}
      >
        <KeyboardArrowLeftIcon />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Select
          value={date.getMonth()}
          onChange={(e) => changeMonth(Number(e.target.value))}
          displayEmpty
          size="small"
          sx={{
            minWidth: 120,
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#46a091",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#46a091",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 350,
              },
            },
          }}
        >
          {months.map((month, index) => {
            const isDisabled =
              maxYear !== null &&
              maxMonth !== null &&
              date.getFullYear() === maxYear &&
              index > maxMonth;

            return (
              <MenuItem key={month} value={index} disabled={isDisabled}>
                {month}
              </MenuItem>
            );
          })}
        </Select>

        <Select
          value={date.getFullYear()}
          onChange={(e) => changeYear(Number(e.target.value))}
          displayEmpty
          size="small"
          sx={{
            minWidth: 100,
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#46a091",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#46a091",
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 350,
              },
            },
          }}
        >
          {years.map((year) => {
            const isDisabled = maxYear !== null && year > maxYear;
            return (
              <MenuItem key={year} value={year} disabled={isDisabled}>
                {year}
              </MenuItem>
            );
          })}
        </Select>
      </Box>

      <Box
        onClick={increaseMonth}
        sx={{
          pointerEvents: nextMonthButtonDisabled ? "none" : "auto",
          cursor: "pointer",
          opacity: nextMonthButtonDisabled ? 0.3 : 1,
        }}
      >
        <KeyboardArrowRightIcon />
      </Box>
    </Box>
  );
};

const CustomDatePicker = ({
  name,
  selected,
  placeholder = "Select date",
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
  disabled = false,
  showYearDropdown = true,
  dateFormat = "MM/dd/yyyy",
  label,
  error,
  helperText,
  formik,
}) => {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const inputWrapperRef = useRef(null);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(0, i).toLocaleString(i18n.language, { month: "long" })
      ),
    [i18n.language]
  );

  const handleChange = (date) => {
    const formattedDate = formatDateToYYYYMMDD(date);
    formik.setFieldValue(name, formattedDate);
    if (!date) {
      formik.setFieldTouched(name, true, true);
    }
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        {label ? (
          <Typography
            color="rgba(47, 43, 61, 0.9)"
            sx={{ fontSize: "15px", fontWeight: 500, mb: "8px" }}
          >
            {label}
          </Typography>
        ) : null}
        <style>
          {`
          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected {
           background-color: #047857 !important; /* Green-500 */
            color: white !important;
          }
          
          .react-datepicker__day--selected:hover,
          .react-datepicker__day--keyboard-selected:hover {
            background-color: #047857 !important; /* Green-700 */
          }

          .react-datepicker__day-names {
            text-align: center;
          }

          .react-datepicker-wrapper {
            width: 100%;
          }
        `}
        </style>
        <DatePicker
          open={open}
          selected={selected}
          onChange={handleChange}
          maxDate={maxDate}
          disabled={disabled}
          placeholderText={placeholder}
          showYearDropdown={showYearDropdown}
          scrollableYearDropdown
          yearDropdownItemNumber={50}
          dateFormat={dateFormat}
          renderCustomHeader={(props) =>
            CustomHeader({ ...props, maxDate, months })
          }
          onFocus={() => {
            setOpen(true);
          }}
          onSelect={handleChange}
          onMonthChange={handleChange}
          onYearChange={handleChange}
          onBlur={formik.handleBlur}
          popperPlacement="top-start"
          className="custom-datepicker-input"
          calendarClassName="custom-datepicker-calendar"
          customInput={
            <InputField
              name={name}
              ref={inputWrapperRef}
              error={error}
              helperText={helperText}
              endAdornment={true}
              endAdornmentIconPath="/icons/calendar-event-icon.svg"
              endAdornmentIconClick={() => setOpen(!open)}
            />
          }
          onClickOutside={(e) => {
            if (
              e.target instanceof Node &&
              document.querySelector(".MuiPopover-root")?.contains(e.target)
            ) {
              return;
            }
            setOpen(false);
          }}
        />
      </Box>
    </>
  );
};

export default CustomDatePicker;
